import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useLanguage } from "@/contexts/LanguageContext";
import { 
  MessageSquare, 
  Search,
  Send,
  CheckCircle,
  XCircle,
  Clock
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "@/components/toast";
import { getMessages } from "@/lib/marketing-api";

const getStatusLabels = (isRTL) => ({
  pending: { label: isRTL ? "قيد الانتظار" : "Pending", icon: Clock, color: "text-yellow-400" },
  sent: { label: isRTL ? "مرسلة" : "Sent", icon: CheckCircle, color: "text-green-400" },
  failed: { label: isRTL ? "فشل" : "Failed", icon: XCircle, color: "text-red-400" }
});

export default function Messages() {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const { t } = useTranslation();
  const { isRTL } = useLanguage();
  const statusLabels = getStatusLabels(isRTL);
  const [statusFilter, setStatusFilter] = useState("all");

  useEffect(() => {
    loadMessages();
  }, []);

  const loadMessages = async () => {
    try {
      const response = await getMessages();
      setMessages(response.data?.data || response.data || []);
    } catch (error) {
      toast.error(t('errors.serverError'));
      setMessages([]);
    } finally {
      setLoading(false);
    }
  };

  const filteredMessages = Array.isArray(messages) ? messages.filter(message => {
    const matchesSearch = (message.content || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === "all" || message.status === statusFilter;
    return matchesSearch && matchesStatus;
  }) : [];

  const getStatusInfo = (status) => {
    return statusLabels[status] || statusLabels.pending;
  };

  // Stats
  const sentCount = Array.isArray(messages) ? messages.filter(m => m.status === "sent").length : 0;
  const pendingCount = Array.isArray(messages) ? messages.filter(m => m.status === "pending").length : 0;
  const failedCount = Array.isArray(messages) ? messages.filter(m => m.status === "failed").length : 0;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="messages-page">
      {/* Header */}
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-3xl font-black text-foreground">{t('marketingFull.messagesTitle')}</h1>
          <p className="text-muted-foreground mt-1">{t('marketingFull.messagesSubtitle')}</p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="stat-card">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-12 h-12 rounded-lg bg-green-500/10 flex items-center justify-center">
              <CheckCircle className="w-6 h-6 text-green-400" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">{t('marketingFull.sentMessages')}</p>
              <p className="text-2xl font-black font-numbers">{sentCount}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="stat-card">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-12 h-12 rounded-lg bg-yellow-500/10 flex items-center justify-center">
              <Clock className="w-6 h-6 text-yellow-400" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">{t('marketingFull.pendingMessages')}</p>
              <p className="text-2xl font-black font-numbers">{pendingCount}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="stat-card">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-12 h-12 rounded-lg bg-red-500/10 flex items-center justify-center">
              <XCircle className="w-6 h-6 text-red-400" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">{t('marketingFull.failedMessages')}</p>
              <p className="text-2xl font-black font-numbers">{failedCount}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder={t('marketingFull.searchMessages')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pe-10"
                data-testid="search-messages-input"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full md:w-[180px]" data-testid="status-filter">
                <SelectValue placeholder={t('marketingFull.status')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('marketingFull.allStatus')}</SelectItem>
                <SelectItem value="sent">{t('marketingFull.sent')}</SelectItem>
                <SelectItem value="pending">{t('marketingFull.pending')}</SelectItem>
                <SelectItem value="failed">{t('marketingFull.failed')}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Messages Table */}
      <Card>
        <CardContent className="p-0">
          {filteredMessages.length === 0 ? (
            <div className="py-12">
              <div className="empty-state">
                <MessageSquare className="empty-state-icon" />
                <h3 className="empty-state-title">{t('marketingFull.noMessages')}</h3>
                <p className="empty-state-description">{t('marketingFull.messagesWillAppear')}</p>
              </div>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('marketingFull.content')}</TableHead>
                  <TableHead>{t('marketingFull.campaign')}</TableHead>
                  <TableHead>{t('marketingFull.status')}</TableHead>
                  <TableHead>{t('marketingFull.sentDate')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredMessages.map((message) => {
                  const statusInfo = getStatusInfo(message.status);
                  const StatusIcon = statusInfo.icon;
                  return (
                    <TableRow key={message.id} data-testid={`message-row-${message.id}`}>
                      <TableCell className="max-w-md">
                        <p className="line-clamp-2">{message.content}</p>
                      </TableCell>
                      <TableCell className="font-numbers">{message.campaign_id}</TableCell>
                      <TableCell>
                        <span className={`flex items-center gap-1 ${statusInfo.color}`}>
                          <StatusIcon className="w-4 h-4" />
                          {statusInfo.label}
                        </span>
                      </TableCell>
                      <TableCell className="font-numbers">
                        {message.sent_at 
                          ? new Date(message.sent_at).toLocaleString(isRTL ? "ar-SA" : "en-US")
                          : "-"
                        }
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
